import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import {
  ArrowLeft, Save, Upload, Trash2, FileText, Download, DollarSign,
  CheckCircle, Circle, Calendar, Printer, Plus, X, UserPlus, Search,
  ExternalLink, ClipboardList, Receipt, HardHat, ListChecks, Camera,
  TrendingUp, TrendingDown, AlertTriangle, StickyNote, BarChart3, Edit, Building2, FileCheck, Eye,
  RefreshCw
} from 'lucide-react';
import InvoiceCoversheetTab from './InvoiceCoversheetTab';
import { toast } from 'sonner';
import { BCS_LOGO_B64 } from './logoBase64';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = v => v != null ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00';
const fmtPct = v => v != null ? `${Number(v).toFixed(1)}%` : '—';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const CREDENTIALS = `CSLB License No. 1066525 (Class A & C-33) | DIR No. 2000002659 | AMPP/NACE Certified | DBIDS Gov Contractor | TWIC Credentialed | DUNS No. 080247052 | WBENC No. WBE2600603`;

function formatDateWithDay(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return `${DAYS[d.getDay()]}, ${(d.getMonth()+1)}/${d.getDate()}/${d.getFullYear()}`;
}

const TABS = [
  { id: 'info', label: 'Info', icon: ClipboardList },
  { id: 'tracking', label: 'Project Status', icon: CheckCircle },
  { id: 'financials', label: 'Financials', icon: DollarSign },
  { id: 'change-orders', label: 'Change Orders', icon: ListChecks },
  { id: 'invoices', label: 'Invoices', icon: Receipt },
  { id: 'coversheets', label: 'Invoice Coversheet', icon: FileCheck },
  { id: 'subcontractors', label: 'Vendors / Subs', icon: HardHat },
  { id: 'logs', label: 'Logs', icon: Calendar },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'schedule', label: 'Schedule', icon: Calendar },
];

const CRMProjectDetail = ({ projectId, onBack }) => {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`${API}/api/crm/projects/${projectId}`, { credentials: 'include' });
      setProject(await res.json());
    } catch { toast.error('Failed to load project'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const silentReload = useCallback(() => load(true), [load]);

  const refreshAll = useCallback(() => {
    load(true);
    setRefreshKey(k => k + 1);
    toast.success('Refreshed');
  }, [load]);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-900" /></div>;
  if (!project) return <p className="text-center py-10 text-gray-500">Project not found</p>;

  const statusBadge = project.project_accepted
    ? { label: 'Active', cls: 'bg-emerald-100 text-emerald-800' }
    : project.bid_submitted
    ? { label: 'Bid Submitted', cls: 'bg-amber-100 text-amber-800' }
    : { label: 'Planning', cls: 'bg-blue-100 text-blue-800' };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="project-back-btn"><ArrowLeft size={16} className="mr-1" /> Back</Button>
          <h2 className="text-lg font-bold text-slate-900">{project.name}</h2>
          {project.project_number && <Badge className="bg-slate-800 text-white text-xs font-mono">{project.project_number}</Badge>}
          <Badge className={statusBadge.cls}>{statusBadge.label}</Badge>
        </div>
        <Button variant="outline" size="sm" onClick={refreshAll} data-testid="project-refresh-btn">
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
      </div>

      {/* Tab Bar */}
      <div className="flex gap-0.5 border-b overflow-x-auto">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            data-testid={`project-tab-${tab.id}`}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id ? 'border-blue-900 text-blue-900' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            <tab.icon size={14} /> {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'info' && <InfoTab key={`info-${refreshKey}`} project={project} projectId={projectId} onReload={silentReload} />}
      {activeTab === 'tracking' && <TrackingTab key={`tracking-${refreshKey}`} project={project} projectId={projectId} onReload={silentReload} />}
      {activeTab === 'financials' && <FinancialsTab key={`fin-${refreshKey}`} projectId={projectId} />}
      {activeTab === 'change-orders' && <ChangeOrdersTab key={`co-${refreshKey}`} projectId={projectId} />}
      {activeTab === 'invoices' && <InvoicesTab key={`inv-${refreshKey}`} projectId={projectId} project={project} />}
      {activeTab === 'coversheets' && <InvoiceCoversheetTab key={`cs-${refreshKey}`} projectId={projectId} project={project} />}
      {activeTab === 'subcontractors' && <SubcontractorsTab key={`subs-${refreshKey}`} projectId={projectId} />}
      {activeTab === 'logs' && <LogsTab key={`logs-${refreshKey}`} projectId={projectId} />}
      {activeTab === 'documents' && <DocumentsTab key={`docs-${refreshKey}`} project={project} projectId={projectId} onReload={silentReload} />}
      {activeTab === 'schedule' && <ScheduleTab key={`sch-${refreshKey}`} schedule={project.schedule || []} projectId={projectId} onReload={silentReload} projectName={project.name} customerCompany={project.customer_company} />}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// INFO TAB (with contact search, company, budget)
// ═══════════════════════════════════════════════════
const InfoTab = ({ project, projectId, onReload }) => {
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', email: '', phone: '' });
  const dropdownRef = useRef(null);
  const [companySearch, setCompanySearch] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const companyDropdownRef = useRef(null);
  const [form, setForm] = useState({
    name: project.name ?? '', customer_company: project.customer_company ?? '',
    customer_contact_id: project.customer_contact_id ?? '',
    description: project.description ?? '', notes: project.notes ?? '',
    budget: project.budget ?? 0, original_contract_value: project.original_contract_value ?? '',
    estimated_cost: project.estimated_cost ?? '',
  });

  // Sync form from project ONLY on reload — replace all values from DB
  const projectRef = useRef(project);
  useEffect(() => {
    // Skip the initial mount (same object reference)
    if (projectRef.current === project) return;
    projectRef.current = project;
    setForm({
      name: project.name ?? '',
      customer_company: project.customer_company ?? '',
      customer_contact_id: project.customer_contact_id ?? '',
      description: project.description ?? '',
      notes: project.notes ?? '',
      budget: project.budget ?? 0,
      original_contract_value: project.original_contract_value ?? '',
      estimated_cost: project.estimated_cost ?? '',
    });
  }, [project]);

  useEffect(() => {
    (async () => {
      const [c, co] = await Promise.all([
        fetch(`${API}/api/crm/contacts?limit=1000`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/crm/companies?limit=500`, { credentials: 'include' }).then(r => r.json()),
      ]);
      setContacts(c.contacts || []); setCompanies(co.companies || []);
    })();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target)) setShowCompanyDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedContact = contacts.find(c => c.id === form.customer_contact_id);
  const filteredContacts = contactSearch
    ? contacts.filter(c => `${c.name} ${c.email}`.toLowerCase().includes(contactSearch.toLowerCase())).slice(0, 15)
    : contacts.slice(0, 15);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      // Only include fields that have actual values (don't send empty strings to overwrite DB)
      if (form.name) payload.name = form.name;
      if (form.customer_company !== undefined) payload.customer_company = form.customer_company;
      if (form.customer_contact_id) payload.customer_contact_id = form.customer_contact_id;
      if (form.description !== undefined) payload.description = form.description;
      if (form.notes !== undefined) payload.notes = form.notes;
      payload.budget = parseFloat(form.budget) || 0;
      // Always send financial values if they're set (including 0)
      if (form.original_contract_value !== '' && form.original_contract_value != null) {
        payload.original_contract_value = parseFloat(form.original_contract_value);
        if (isNaN(payload.original_contract_value)) delete payload.original_contract_value;
      }
      if (form.estimated_cost !== '' && form.estimated_cost != null) {
        payload.estimated_cost = parseFloat(form.estimated_cost);
        if (isNaN(payload.estimated_cost)) delete payload.estimated_cost;
      }
      const res = await fetch(`${API}/api/crm/projects/${projectId}`, { credentials: 'include',
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Save failed'); }
      toast.success('Saved'); onReload();
    } catch (e) { toast.error(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleAddNewContact = async () => {
    if (!newContact.email) { toast.error('Email is required'); return; }
    try {
      const res = await fetch(`${API}/api/crm/contacts`, { credentials: 'include',
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newContact, source: 'project' }),
      });
      const created = await res.json();
      if (!res.ok) throw new Error(created.detail);
      setForm(f => ({ ...f, customer_contact_id: created.id }));
      setShowAddContact(false); setNewContact({ name: '', email: '', phone: '' });
      toast.success('Contact added'); onReload();
    } catch (err) { toast.error(err.message || 'Failed'); }
  };

  return (
    <Card><CardContent className="pt-5 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
          <input data-testid="project-name" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" />
        </div>
        <div ref={companyDropdownRef} className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">Customer Company</label>
          <div className="relative">
            <Building2 className="absolute left-3 top-2.5 text-gray-400" size={14} />
            <input data-testid="project-company"
              value={showCompanyDropdown ? companySearch : form.customer_company}
              onChange={e => { setCompanySearch(e.target.value); setForm(f => ({...f, customer_company: e.target.value})); setShowCompanyDropdown(true); }}
              onFocus={() => { setCompanySearch(form.customer_company); setShowCompanyDropdown(true); }}
              placeholder="Search or type company name..."
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" />
          </div>
          {showCompanyDropdown && (
            <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-52 overflow-y-auto">
              {companies.filter(co => !companySearch || co.name.toLowerCase().includes(companySearch.toLowerCase())).slice(0, 15).map(co => (
                <button key={co.id} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50"
                  onClick={() => { setForm(f => ({...f, customer_company: co.name})); setShowCompanyDropdown(false); setCompanySearch(''); }}>
                  <span className="font-medium">{co.name}</span>
                  {co.industry && <span className="text-gray-400 ml-2 text-xs">{co.industry}</span>}
                </button>
              ))}
              {companySearch && !companies.some(co => co.name.toLowerCase() === companySearch.toLowerCase()) && (
                <button type="button" className="w-full text-left px-3 py-2 text-sm text-blue-600 font-medium hover:bg-blue-50 border-t flex items-center gap-2"
                  onClick={() => { setForm(f => ({...f, customer_company: companySearch})); setShowCompanyDropdown(false); }}>
                  <Plus size={14} /> Use "{companySearch}"
                </button>
              )}
            </div>
          )}
        </div>
        <div ref={dropdownRef} className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">Customer Contact</label>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 text-gray-400" size={14} />
            <input data-testid="project-contact-search"
              value={showDropdown ? contactSearch : (selectedContact ? `${selectedContact.name || selectedContact.email}` : '')}
              onChange={e => { setContactSearch(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              placeholder="Search contacts..."
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" />
          </div>
          {showDropdown && (
            <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-52 overflow-y-auto">
              {filteredContacts.map(c => (
                <button key={c.id} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex justify-between"
                  onClick={() => { setForm(f => ({...f, customer_contact_id: c.id})); setShowDropdown(false); setContactSearch(''); }}>
                  <span><span className="font-medium">{c.name || c.email}</span>{c.name && <span className="text-gray-400 ml-1">{c.email}</span>}</span>
                </button>
              ))}
              <button type="button" className="w-full text-left px-3 py-2 text-sm text-blue-600 font-medium hover:bg-blue-50 border-t flex items-center gap-2"
                onClick={() => { setShowAddContact(true); setShowDropdown(false); }}>
                <UserPlus size={14} /> Add new contact...
              </button>
            </div>
          )}
        </div>
        <CurrencyInput label="Original Contract Value" value={form.original_contract_value} onChange={v => setForm(f => ({ ...f, original_contract_value: v }))} testId="project-ocv" />
        <div>
          <label className="block text-xs text-slate-500 mb-1">Estimated Cost</label>
          <div className="relative">
            <span className="absolute left-3 top-2.5 text-gray-400 text-sm">$</span>
            <input type="text" readOnly data-testid="project-est-cost"
              value={form.estimated_cost !== '' && form.estimated_cost != null ? Number(form.estimated_cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
              className="w-full pl-7 pr-3 py-2 border rounded-lg text-sm bg-gray-50 text-gray-500 cursor-not-allowed" />
          </div>
          <p className="text-xs text-gray-400 mt-0.5">Auto-populated from Financials → Cost Tracking</p>
        </div>
      </div>
      {showAddContact && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2"><UserPlus size={16} /> Add New Contact</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input value={newContact.name} onChange={e => setNewContact(c => ({...c, name: e.target.value}))} placeholder="Name" className="px-3 py-2 border rounded-lg text-sm" />
            <input type="email" value={newContact.email} onChange={e => setNewContact(c => ({...c, email: e.target.value}))} placeholder="Email *" className="px-3 py-2 border rounded-lg text-sm" />
            <input value={newContact.phone} onChange={e => setNewContact(c => ({...c, phone: e.target.value}))} placeholder="Phone" className="px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={handleAddNewContact}><Plus size={14} className="mr-1" /> Add</Button>
            <Button size="sm" variant="outline" onClick={() => setShowAddContact(false)}>Cancel</Button>
          </div>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={3} className="w-full px-3 py-2 border rounded-lg text-sm" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={3} className="w-full px-3 py-2 border rounded-lg text-sm" />
      </div>
      <Button onClick={handleSave} disabled={saving} className="bg-blue-900 hover:bg-blue-800" data-testid="save-project-info">
        <Save size={16} className="mr-1" /> {saving ? 'Saving...' : 'Save Info'}
      </Button>
    </CardContent></Card>
  );
};

// ═══════════════════════════════════════════════════
// PROJECT STATUS TAB — Submitted / Accepted / Withdrawn / Completed / Invoicing / Closed
// ═══════════════════════════════════════════════════
const TrackingTab = ({ project, projectId, onReload }) => {
  const [bidDate, setBidDate] = useState(project.bid_submitted_date || '');
  const [acceptDate, setAcceptDate] = useState(project.project_accepted_date || '');
  const [withdrawnDate, setWithdrawnDate] = useState(project.project_withdrawn_date || '');
  const [completedDate, setCompletedDate] = useState(project.project_completed_date || '');
  const [invoicingDate, setInvoicingDate] = useState(project.project_invoicing_date || '');
  const [closedDate, setClosedDate] = useState(project.project_closed_date || '');

  const handleToggle = async (field, newVal, dateVal) => {
    const dateField = `${field}_date`;
    const update = { [field]: newVal, [dateField]: dateVal || null };
    try {
      await fetch(`${API}/api/crm/projects/${projectId}`, { credentials: 'include',
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(update),
      });
      await onReload();
    } catch { toast.error('Update failed'); }
  };

  const handleFileUpload = async (e, category) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/api/crm/projects/${projectId}/files?category=${category}`, { credentials: 'include', method: 'POST', body: fd });
      if (!res.ok) throw new Error();
      toast.success('File uploaded'); await onReload();
    } catch { toast.error('Upload failed'); }
    e.target.value = '';
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await fetch(`${API}/api/crm/projects/${projectId}/files/${fileId}`, { credentials: 'include', method: 'DELETE' });
      toast.success('Deleted'); await onReload();
    } catch { toast.error('Delete failed'); }
  };

  // Reusable status row component
  const StatusRow = ({ title, field, dateVal, setDateVal, isOn, color = 'emerald', testId, children }) => (
    <Card><CardContent className="pt-5">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <div className="flex items-center gap-3 mt-2">
            <label className="text-sm text-gray-600">Date:</label>
            <input type="date" value={dateVal} onChange={e => setDateVal(e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-sm" />
          </div>
        </div>
        <button onClick={() => handleToggle(field, !isOn, dateVal)} data-testid={testId}
          className={`relative w-14 h-7 rounded-full transition-colors duration-200 flex-shrink-0 ${isOn ? `bg-${color}-500` : 'bg-gray-300'}`}>
          <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-200 ${isOn ? 'left-[30px]' : 'left-0.5'}`} />
        </button>
      </div>
      {isOn && project[`${field}_date`] && (
        <p className={`text-sm text-${color}-600 mt-2 font-medium`}>On: {formatDateWithDay(project[`${field}_date`])}</p>
      )}
      {children}
    </CardContent></Card>
  );

  return (
    <div className="space-y-4">
      <StatusRow title="Estimate / Bid Submitted" field="bid_submitted" dateVal={bidDate} setDateVal={setBidDate}
        isOn={project.bid_submitted} color="amber" testId="toggle-bid-submitted" />

      <StatusRow title="Project Accepted" field="project_accepted" dateVal={acceptDate} setDateVal={setAcceptDate}
        isOn={project.project_accepted} color="emerald" testId="toggle-project-accepted">
        {project.project_number && (
          <p className="text-sm text-slate-700 mt-3 mb-3">Project Number: <span className="font-mono font-bold text-lg">{project.project_number}</span></p>
        )}
        {project.project_accepted && (
          <div className="border-t pt-4 mt-2">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Customer Purchase Order</h4>
            {project.customer_po ? <FileRow file={project.customer_po} onDelete={handleDeleteFile} />
              : <UploadBtn category="customer_po" label="Upload Customer PO" onChange={handleFileUpload} testId="upload-customer-po" />}
          </div>
        )}
      </StatusRow>

      <StatusRow title="Bid Withdrawn" field="project_withdrawn" dateVal={withdrawnDate} setDateVal={setWithdrawnDate}
        isOn={project.project_withdrawn} color="orange" testId="toggle-project-withdrawn" />

      <StatusRow title="Project Completed (Work Done)" field="project_completed" dateVal={completedDate} setDateVal={setCompletedDate}
        isOn={project.project_completed} color="sky" testId="toggle-project-completed" />

      <StatusRow title="Invoicing (Billing in Progress)" field="project_invoicing" dateVal={invoicingDate} setDateVal={setInvoicingDate}
        isOn={project.project_invoicing} color="indigo" testId="toggle-project-invoicing" />

      <StatusRow title="Project Closed (Fully Paid)" field="project_closed" dateVal={closedDate} setDateVal={setClosedDate}
        isOn={project.project_closed} color="slate" testId="toggle-project-closed" />
    </div>
  );
};

// ═══════════════════════════════════════════════════
// FINANCIALS TAB
// ═══════════════════════════════════════════════════
const FinancialsTab = ({ projectId }) => {
  const [fin, setFin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fRes, pRes, subRes] = await Promise.all([
        fetch(`${API}/api/crm/projects/${projectId}/financials`, { credentials: 'include' }),
        fetch(`${API}/api/crm/projects/${projectId}`, { credentials: 'include' }),
        fetch(`${API}/api/crm/projects/${projectId}/subcontractors`, { credentials: 'include' }),
      ]);
      const fData = await fRes.json();
      const pData = await pRes.json();
      const subData = await subRes.json();
      setFin(fData);
      // Build cost tracking from subcontractors + existing manual expenses
      const subs = (subData.assignments || []);
      const subExpenses = subs.map(s => ({
        description: `${s.sub_info?.company_name || 'Sub'}${s.sub_info?.trade ? ' (' + s.sub_info.trade + ')' : ''}${s.bcs_po_number ? ' — PO: ' + s.bcs_po_number : ''}`,
        amount: String(s.committed_cost || s.contract_amount || 0),
        from_sub: true,
      }));
      const manualExpenses = (pData.project_expenses || []).filter(e => !e.from_sub);
      const allExpenses = [...subExpenses, ...(manualExpenses.length > 0 ? manualExpenses : [{ description: '', amount: '' }])];
      setForm({
        actual_cost: pData.actual_cost ?? '',
        committed_cost: pData.committed_cost ?? '',
        project_expenses: allExpenses,
      });
    } catch { toast.error('Failed to load financials'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    const expenseTotal = (form.project_expenses || []).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    const payload = {
      last_cost_update_at: new Date().toISOString(),
      cost_to_complete: expenseTotal,
      estimated_cost: expenseTotal,
      project_expenses: (form.project_expenses || []).filter(e => e.description || e.amount),
    };
    try {
      await fetch(`${API}/api/crm/projects/${projectId}`, { credentials: 'include',
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      toast.success('Costs updated');
      setEditing(false); load();
    } catch { toast.error('Save failed'); }
  };

  if (loading) return <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto" /></div>;
  if (!fin) return <p className="text-gray-500 text-center py-8">No financial data</p>;

  const profitColor = (fin.projected_profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600';
  const fadeColor = (fin.profit_fade || 0) <= 0 ? 'text-emerald-600' : 'text-red-600';

  return (
    <div className="space-y-4">
      <Card data-testid="financial-contract-section"><CardContent className="pt-5">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Contract Value</h3>
        <div className="grid grid-cols-3 gap-4">
          <FinMetric label="Original Contract" value={fmt(fin.original_contract_value)} />
          <FinMetric label="Approved COs" value={fmt(fin.approved_change_order_value)} sub={`Pending: ${fmt(fin.pending_change_order_value)}`} />
          <FinMetric label="Revised Contract" value={fmt(fin.revised_contract_value)} bold />
        </div>
      </CardContent></Card>
      <Card data-testid="financial-cost-section"><CardContent className="pt-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Cost Tracking</h3>
          <Button variant="outline" size="sm" onClick={() => setEditing(!editing)} data-testid="edit-costs-btn">
            <Edit size={14} className="mr-1" /> {editing ? 'Cancel' : 'Update Costs'}
          </Button>
        </div>
        {editing ? (
          <div className="space-y-3">
            <div className="border rounded-lg p-3 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-600">Project Expenses</p>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setForm(f => ({ ...f, project_expenses: [...(f.project_expenses || []), { description: '', amount: '' }] }))} data-testid="add-expense-btn">
                  <Plus size={12} className="mr-1" /> Add Line
                </Button>
              </div>
              <div className="space-y-2">
                {(form.project_expenses || []).map((exp, idx) => (
                  <div key={idx} className={`flex items-center gap-2 ${exp.from_sub ? 'bg-blue-50 px-2 py-1 rounded' : ''}`}>
                    <input placeholder="Description" value={exp.description || ''} className={`flex-1 px-2 py-1.5 border rounded text-sm ${exp.from_sub ? 'bg-blue-50 text-slate-600' : ''}`}
                      readOnly={!!exp.from_sub}
                      onChange={e => { if (exp.from_sub) return; const arr = [...form.project_expenses]; arr[idx] = { ...arr[idx], description: e.target.value }; setForm(f => ({ ...f, project_expenses: arr })); }} data-testid={`expense-desc-${idx}`} />
                    <div className="relative w-36">
                      <span className="absolute left-2 top-1.5 text-gray-400 text-sm">$</span>
                      <input type="number" step="0.01" placeholder="0.00" value={exp.amount || ''} className={`w-full pl-6 pr-2 py-1.5 border rounded text-sm ${exp.from_sub ? 'bg-blue-50 text-slate-600' : ''}`}
                        readOnly={!!exp.from_sub}
                        onChange={e => { if (exp.from_sub) return; const arr = [...form.project_expenses]; arr[idx] = { ...arr[idx], amount: e.target.value }; setForm(f => ({ ...f, project_expenses: arr })); }} data-testid={`expense-amt-${idx}`} />
                    </div>
                    {!exp.from_sub && form.project_expenses.filter(e => !e.from_sub).length > 1 && (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => setForm(f => ({ ...f, project_expenses: f.project_expenses.filter((_, i) => i !== idx) }))} data-testid={`expense-del-${idx}`}>
                        <X size={14} />
                      </Button>
                    )}
                    {exp.from_sub && <Badge className="text-[10px] bg-blue-100 text-blue-700 flex-shrink-0">From Subs</Badge>}
                  </div>
                ))}
              </div>
              <div className="mt-2 text-right text-sm font-semibold text-slate-700">
                Total: {fmt((form.project_expenses || []).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0))}
              </div>
            </div>
            <Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={handleSave} data-testid="save-costs-btn"><Save size={14} className="mr-1" /> Save Costs</Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <FinMetric label="Project Expenses" value={fmt(fin.cost_to_complete)} />
            <FinMetric label="Projected Final Cost" value={fmt(fin.projected_final_cost)} bold />
          </div>
        )}
      </CardContent></Card>
      <Card data-testid="financial-profit-section"><CardContent className="pt-5">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Profit Analysis</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <FinMetric label="Estimated Profit" value={fin.estimated_profit != null ? fmt(fin.estimated_profit) : '—'} />
          <div><p className="text-[10px] text-gray-500">Projected Profit</p><p className={`text-lg font-bold ${profitColor}`} data-testid="projected-profit">{fmt(fin.projected_profit)}</p></div>
          <div><p className="text-[10px] text-gray-500">Projected Margin</p><p className={`text-lg font-bold ${profitColor}`} data-testid="projected-margin">{fmtPct(fin.projected_margin_percent)}</p></div>
          <div><p className="text-[10px] text-gray-500 flex items-center gap-1">Profit Fade {(fin.profit_fade || 0) > 0 && <AlertTriangle size={12} className="text-red-500" />}</p><p className={`text-lg font-bold ${fadeColor}`} data-testid="profit-fade">{fin.profit_fade != null ? fmt(fin.profit_fade) : '—'}</p></div>
        </div>
      </CardContent></Card>
      <Card data-testid="financial-billing-section"><CardContent className="pt-5">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Billing</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <FinMetric label="Billed to Date" value={fmt(fin.billed_to_date)} />
          <FinMetric label="Collected" value={fmt(fin.collected_to_date)} />
          <FinMetric label="Outstanding" value={fmt(fin.outstanding_receivables)} warn={fin.outstanding_receivables > 0} />
        </div>
      </CardContent></Card>

      {/* P&L REPORT */}
      <Card data-testid="financial-pnl-section"><CardContent className="pt-5">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
          <BarChart3 size={14} /> Profit & Loss Report
        </h3>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold text-slate-700">Line Item</th>
                <th className="text-right px-4 py-2.5 font-semibold text-slate-700">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t bg-emerald-50">
                <td className="px-4 py-2.5 font-semibold text-emerald-800">Total Revenue (Revised Contract Value)</td>
                <td className="px-4 py-2.5 text-right font-bold text-emerald-800">{fmt(fin.revised_contract_value)}</td>
              </tr>
              <tr className="border-t bg-slate-50">
                <td className="px-4 py-2 text-xs text-slate-500 pl-6">Original Contract Value</td>
                <td className="px-4 py-2 text-right text-xs text-slate-500">{fmt(fin.original_contract_value)}</td>
              </tr>
              <tr className="bg-slate-50">
                <td className="px-4 py-2 text-xs text-slate-500 pl-6">Approved Change Orders</td>
                <td className="px-4 py-2 text-right text-xs text-slate-500">{fmt(fin.approved_change_order_value)}</td>
              </tr>
              <tr className="border-t">
                <td className="px-4 py-2.5 font-semibold text-red-700" colSpan={2}>Less: Expenses</td>
              </tr>
              {(fin.sub_expense_lines || []).length > 0 ? (
                <>
                  <tr className="bg-gray-50">
                    <td className="px-4 py-2 text-xs font-medium text-slate-600 pl-6">Vendor / Subcontractor Expenses</td>
                    <td className="px-4 py-2 text-right text-xs font-medium text-red-600">({fmt(fin.total_sub_expenses)})</td>
                  </tr>
                  {fin.sub_expense_lines.map((sub, i) => (
                    <tr key={i} className="bg-gray-50">
                      <td className="px-4 py-1.5 text-xs text-slate-500 pl-10">{sub.name}{sub.trade ? ` — ${sub.trade}` : ''}{sub.scope ? `: ${sub.scope}` : ''}</td>
                      <td className="px-4 py-1.5 text-right text-xs text-slate-500">{fmt(sub.committed_cost)}</td>
                    </tr>
                  ))}
                </>
              ) : (
                <tr className="bg-gray-50">
                  <td className="px-4 py-2 text-xs text-gray-400 pl-6">No vendor / subcontractor expenses</td>
                  <td className="px-4 py-2 text-right text-xs text-gray-400">$0.00</td>
                </tr>
              )}
              {fin.actual_cost > 0 && (
                <tr className="bg-gray-50 border-t">
                  <td className="px-4 py-2 text-xs font-medium text-slate-600 pl-6">Direct / Other Costs</td>
                  <td className="px-4 py-2 text-right text-xs font-medium text-red-600">({fmt(Math.max(0, (fin.actual_cost || 0) - (fin.total_sub_expenses || 0)))})</td>
                </tr>
              )}
              <tr className="border-t-2 border-slate-300">
                <td className="px-4 py-3 font-bold text-slate-900">Net Profit / (Loss)</td>
                <td className={`px-4 py-3 text-right font-bold text-lg ${(fin.projected_profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(fin.projected_profit)}</td>
              </tr>
              <tr className="bg-slate-50">
                <td className="px-4 py-2 text-xs text-slate-500 pl-6">Projected Margin</td>
                <td className="px-4 py-2 text-right text-xs text-slate-500">{fmtPct(fin.projected_margin_percent)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent></Card>
    </div>
  );
};

const FinMetric = ({ label, value, sub, bold, warn }) => (
  <div>
    <p className="text-[10px] text-gray-500">{label}</p>
    <p className={`text-lg ${bold ? 'font-bold text-slate-900' : 'font-semibold text-slate-700'} ${warn ? 'text-amber-600' : ''}`}>{value}</p>
    {sub && <p className="text-[10px] text-gray-400">{sub}</p>}
  </div>
);

const CurrencyInput = ({ label, value, onChange, testId }) => {
  const [raw, setRaw] = React.useState('');
  const [focused, setFocused] = React.useState(false);
  const display = value !== '' && value != null
    ? Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '';
  return (
    <div>
      {label && <Label className="text-xs text-slate-500">{label}</Label>}
      <div className="relative">
        <span className="absolute left-3 top-2.5 text-gray-400 text-sm">$</span>
        <input type="text" data-testid={testId}
          value={focused ? raw : display}
          onFocus={() => { setFocused(true); setRaw(value !== '' && value != null ? String(value) : ''); }}
          onBlur={() => setFocused(false)}
          onChange={e => { const v = e.target.value.replace(/[^0-9.,]/g, ''); setRaw(v); const parsed = parseFloat(v.replace(/,/g, '')); onChange(isNaN(parsed) ? '' : parsed); }}
          placeholder="0.00" className="w-full pl-7 pr-3 py-2 border rounded-lg text-sm" />
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════
// CHANGE ORDERS TAB
// ═══════════════════════════════════════════════════
const ChangeOrdersTab = ({ projectId }) => {
  const [cos, setCos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const emptyForm = { title: '', description: '', co_type: 'Owner Change', pricing_type: 'Lump Sum', status: 'Draft', requested_amount: '', approved_amount: '', cost_estimate: '', submitted_date: '', submitted_to: '', approved_date: '', approved_by: '', rejection_reason: '', schedule_impact_days: '', notes: '' };
  const [form, setForm] = useState({ ...emptyForm });

  const load = useCallback(async () => {
    setLoading(true);
    try { setCos((await (await fetch(`${API}/api/crm/projects/${projectId}/change-orders`, { credentials: 'include' })).json()).change_orders || []); }
    catch { toast.error('Failed'); } finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    const payload = { ...form, requested_amount: parseFloat(form.requested_amount) || 0, approved_amount: parseFloat(form.approved_amount) || 0, cost_estimate: parseFloat(form.cost_estimate) || 0, schedule_impact_days: parseInt(form.schedule_impact_days) || 0 };
    try {
      const url = editId ? `${API}/api/crm/projects/${projectId}/change-orders/${editId}` : `${API}/api/crm/projects/${projectId}/change-orders`;
      const res = await fetch(url, { method: editId ? 'PUT' : 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail); }
      toast.success(editId ? 'CO updated' : 'CO created');
      setShowForm(false); setEditId(null); setForm({ ...emptyForm }); load();
    } catch (e) { toast.error(e.message); }
  };
  const startEdit = (co) => { setForm({ title: co.title||'', description: co.description||'', co_type: co.co_type||'Owner Change', pricing_type: co.pricing_type||'Lump Sum', status: co.status||'Draft', requested_amount: co.requested_amount||'', approved_amount: co.approved_amount||'', cost_estimate: co.cost_estimate||'', submitted_date: co.submitted_date||'', submitted_to: co.submitted_to||'', approved_date: co.approved_date||'', approved_by: co.approved_by||'', rejection_reason: co.rejection_reason||'', schedule_impact_days: co.schedule_impact_days||'', notes: co.notes||'' }); setEditId(co.id); setShowForm(true); };
  const handleDelete = async (id) => { if (!window.confirm('Delete?')) return; await fetch(`${API}/api/crm/projects/${projectId}/change-orders/${id}`, { credentials: 'include', method: 'DELETE' }); toast.success('Deleted'); load(); };
  const statusColor = { Draft: 'bg-gray-100 text-gray-700', Submitted: 'bg-blue-100 text-blue-700', Approved: 'bg-emerald-100 text-emerald-700', Rejected: 'bg-red-100 text-red-700' };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">{cos.length} change orders</p>
        <Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={() => { setForm({ ...emptyForm }); setEditId(null); setShowForm(true); }} data-testid="add-co-btn"><Plus size={14} className="mr-1" /> New CO</Button>
      </div>
      {showForm && (
        <Card className="border-blue-200"><CardContent className="pt-5 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="col-span-2"><Label className="text-xs text-slate-500">Title *</Label><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="co-title" /></div>
            <div><Label className="text-xs text-slate-500">Type</Label><select value={form.co_type} onChange={e => setForm(f => ({ ...f, co_type: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm bg-white">{['Owner Change','Internal','Backcharge'].map(t=><option key={t}>{t}</option>)}</select></div>
            <div><Label className="text-xs text-slate-500">Pricing</Label><select value={form.pricing_type} onChange={e => setForm(f => ({ ...f, pricing_type: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm bg-white">{['Lump Sum','T&M'].map(t=><option key={t}>{t}</option>)}</select></div>
            <CurrencyInput label="Requested Amount" value={form.requested_amount} onChange={v => setForm(f => ({ ...f, requested_amount: v }))} testId="co-requested" />
            <CurrencyInput label="Approved Amount" value={form.approved_amount} onChange={v => setForm(f => ({ ...f, approved_amount: v }))} testId="co-approved" />
            <CurrencyInput label="Cost Estimate" value={form.cost_estimate} onChange={v => setForm(f => ({ ...f, cost_estimate: v }))} testId="co-cost-est" />
            <div><Label className="text-xs text-slate-500">Status</Label><select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="co-status">{['Draft','Submitted','Approved','Rejected'].map(s=><option key={s}>{s}</option>)}</select></div>
            <div><Label className="text-xs text-slate-500">Submitted Date</Label><input type="date" value={form.submitted_date||''} onChange={e => setForm(f => ({ ...f, submitted_date: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
            <div><Label className="text-xs text-slate-500">Submitted To</Label><input value={form.submitted_to} onChange={e => setForm(f => ({ ...f, submitted_to: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
            <div><Label className="text-xs text-slate-500">Approved Date</Label><input type="date" value={form.approved_date||''} onChange={e => setForm(f => ({ ...f, approved_date: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
            <div><Label className="text-xs text-slate-500">Approved By</Label><input value={form.approved_by} onChange={e => setForm(f => ({ ...f, approved_by: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
          </div>
          <div><Label className="text-xs text-slate-500">Description</Label><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
          <div className="flex gap-2"><Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={handleSave} data-testid="save-co-btn"><Save size={14} className="mr-1" /> {editId?'Update':'Create'}</Button><Button size="sm" variant="outline" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Button></div>
        </CardContent></Card>
      )}
      {loading ? <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto" /></div>
      : cos.length === 0 ? <Card><CardContent className="py-8 text-center text-gray-400">No change orders yet</CardContent></Card>
      : <div className="space-y-2">{cos.map(co => (
        <Card key={co.id} data-testid={`co-row-${co.id}`}><CardContent className="py-3 px-4"><div className="flex items-center justify-between">
          <div className="flex-1 min-w-0"><div className="flex items-center gap-2"><span className="text-xs font-mono text-gray-400">CO-{String(co.co_number).padStart(3,'0')}</span><Badge className={`text-[10px] ${statusColor[co.status]||'bg-gray-100'}`}>{co.status}</Badge><Badge className="text-[10px] bg-slate-100 text-slate-600">{co.co_type}</Badge></div><p className="text-sm font-medium text-slate-900 mt-0.5">{co.title}</p></div>
          <div className="flex items-center gap-4 flex-shrink-0 ml-3"><div className="text-right"><p className="text-xs text-gray-500">Requested</p><p className="text-sm font-semibold">{fmt(co.requested_amount)}</p></div><div className="text-right"><p className="text-xs text-gray-500">Approved</p><p className="text-sm font-semibold text-emerald-600">{co.status==='Approved'?fmt(co.approved_amount):'—'}</p></div>
            <div className="flex gap-1"><Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => startEdit(co)}><Edit size={13} /></Button><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => handleDelete(co.id)}><Trash2 size={13} /></Button></div></div>
        </div></CardContent></Card>
      ))}</div>}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// INVOICES TAB
// ═══════════════════════════════════════════════════
const InvoicesTab = ({ projectId, project }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  const emptyForm = { invoice_number:'', invoice_date:'', period_start:'', period_end:'', amount:'', retainage_amount:'', due_date:'', paid_date:'', status:'Draft', billing_type:'Progress', percent_complete:'', schedule_of_values_line:'', is_retention_invoice:false, notes:'' };
  const makeNewForm = () => ({
    ...emptyForm,
    invoice_number: '',
    amount: project?.original_contract_value || '',
    notes: [project?.customer_company && `Customer: ${project.customer_company}`, project?.project_number && `Project: ${project.project_number}`].filter(Boolean).join('\n'),
  });
  const [form, setForm] = useState({ ...emptyForm });

  const load = useCallback(async () => {
    setLoading(true);
    try { setInvoices((await (await fetch(`${API}/api/crm/projects/${projectId}/invoices`, { credentials: 'include' })).json()).invoices || []); }
    catch { toast.error('Failed'); } finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    const payload = { ...form, amount: parseFloat(form.amount)||0, retainage_amount: parseFloat(form.retainage_amount)||0, percent_complete: parseFloat(form.percent_complete)||0 };
    if (!form.paid_date) delete payload.paid_date;
    try {
      const url = editId ? `${API}/api/crm/projects/${projectId}/invoices/${editId}` : `${API}/api/crm/projects/${projectId}/invoices`;
      const res = await fetch(url, { method: editId?'PUT':'POST', credentials: 'include', headers: { 'Content-Type':'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail); }
      toast.success(editId?'Updated':'Created'); setShowForm(false); setEditId(null); setForm({ ...emptyForm }); load();
    } catch (e) { toast.error(e.message); }
  };
  const startEdit = (inv) => { setForm({ invoice_number:inv.invoice_number||'', invoice_date:inv.invoice_date||'', period_start:inv.period_start||'', period_end:inv.period_end||'', amount:inv.amount||'', retainage_amount:inv.retainage_amount||'', due_date:inv.due_date||'', paid_date:inv.paid_date||'', status:inv.status||'Draft', billing_type:inv.billing_type||'Progress', percent_complete:inv.percent_complete||'', schedule_of_values_line:inv.schedule_of_values_line||'', is_retention_invoice:inv.is_retention_invoice||false, notes:inv.notes||'' }); setEditId(inv.id); setShowForm(true); };
  const handleDelete = async (id) => { if (!window.confirm('Delete?')) return; await fetch(`${API}/api/crm/projects/${projectId}/invoices/${id}`, { credentials: 'include', method:'DELETE' }); toast.success('Deleted'); load(); };
  const statusColor = { Draft:'bg-gray-100 text-gray-700', Submitted:'bg-blue-100 text-blue-700', Paid:'bg-emerald-100 text-emerald-700', Overdue:'bg-red-100 text-red-700' };
  const totalBilled = invoices.filter(i=>i.status!=='Draft').reduce((s,i)=>s+(i.amount||0), 0);
  const totalCollected = invoices.filter(i=>i.paid_date).reduce((s,i)=>s+(i.amount||0), 0);

  return (
    <div className="space-y-3">
      {/* View-only modal */}
      {viewInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setViewInvoice(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Eye size={16} className="text-blue-600" />
                <h3 className="font-semibold text-slate-900">Invoice Details</h3>
              </div>
              <button onClick={() => setViewInvoice(null)} className="text-gray-400 hover:text-gray-600" data-testid="close-inv-view"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-gray-500">BCS Invoice #</p><p className="text-sm font-medium">{viewInvoice.invoice_number || '—'}</p></div>
                <div><p className="text-xs text-gray-500">Status</p><Badge className={`text-xs ${statusColor[viewInvoice.status]||'bg-gray-100'}`}>{viewInvoice.status}</Badge></div>
                <div><p className="text-xs text-gray-500">Invoice Date</p><p className="text-sm">{viewInvoice.invoice_date || '—'}</p></div>
                <div><p className="text-xs text-gray-500">Due Date</p><p className="text-sm">{viewInvoice.due_date || '—'}</p></div>
                <div><p className="text-xs text-gray-500">Amount</p><p className="text-sm font-bold text-slate-900">{fmt(viewInvoice.amount)}</p></div>
                <div><p className="text-xs text-gray-500">Retainage</p><p className="text-sm">{fmt(viewInvoice.retainage_amount)}</p></div>
                <div><p className="text-xs text-gray-500">Billing Type</p><p className="text-sm">{viewInvoice.billing_type || '—'}</p></div>
                <div><p className="text-xs text-gray-500">Paid Date</p><p className="text-sm">{viewInvoice.paid_date || '—'}</p></div>
              </div>
              {viewInvoice.notes && <div><p className="text-xs text-gray-500">Notes</p><p className="text-sm whitespace-pre-wrap">{viewInvoice.notes}</p></div>}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => { setViewInvoice(null); startEdit(viewInvoice); }} data-testid="inv-view-edit-btn"><Edit size={13} className="mr-1" /> Edit</Button>
              <Button size="sm" variant="ghost" onClick={() => setViewInvoice(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span>{invoices.length} invoices</span>
          <span>Billed: <strong className="text-slate-700">{fmt(totalBilled)}</strong></span>
          <span>Collected: <strong className="text-emerald-600">{fmt(totalCollected)}</strong></span>
          <span>Outstanding: <strong className="text-amber-600">{fmt(totalBilled - totalCollected)}</strong></span>
        </div>
        <Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={() => { setForm(makeNewForm()); setEditId(null); setShowForm(true); }} data-testid="add-invoice-btn"><Plus size={14} className="mr-1" /> New Invoice</Button>
      </div>
      {showForm && (
        <Card className="border-blue-200"><CardContent className="pt-5 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><Label className="text-xs text-slate-500">BCS Invoice #</Label><input value={form.invoice_number} onChange={e => setForm(f => ({...f, invoice_number:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="inv-number" placeholder="Enter manually" /></div>
            <div><Label className="text-xs text-slate-500">Invoice Date</Label><input type="date" value={form.invoice_date} onChange={e => setForm(f => ({...f, invoice_date:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
            <CurrencyInput label="Amount *" value={form.amount} onChange={v => setForm(f => ({...f, amount:v}))} testId="inv-amount" />
            <CurrencyInput label="Retainage" value={form.retainage_amount} onChange={v => setForm(f => ({...f, retainage_amount:v}))} testId="inv-retainage" />
            <div><Label className="text-xs text-slate-500">Billing Type</Label><select value={form.billing_type} onChange={e => setForm(f => ({...f, billing_type:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm bg-white">{['Progress','T&M','Final'].map(t=><option key={t}>{t}</option>)}</select></div>
            <div><Label className="text-xs text-slate-500">Status</Label><select value={form.status} onChange={e => setForm(f => ({...f, status:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="inv-status">{['Draft','Submitted','Paid','Overdue'].map(s=><option key={s}>{s}</option>)}</select></div>
            <div><Label className="text-xs text-slate-500">Due Date</Label><input type="date" value={form.due_date} onChange={e => setForm(f => ({...f, due_date:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
            <div><Label className="text-xs text-slate-500">Paid Date</Label><input type="date" value={form.paid_date} onChange={e => setForm(f => ({...f, paid_date:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
          </div>
          <div className="flex gap-2"><Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={handleSave} data-testid="save-inv-btn"><Save size={14} className="mr-1" /> {editId?'Update':'Create'}</Button><Button size="sm" variant="outline" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Button></div>
        </CardContent></Card>
      )}
      {loading ? <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto" /></div>
      : invoices.length === 0 ? <Card><CardContent className="py-8 text-center text-gray-400">No invoices yet</CardContent></Card>
      : <div className="space-y-2">{invoices.map(inv => (
        <Card key={inv.id} data-testid={`inv-row-${inv.id}`}><CardContent className="py-3 px-4"><div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Receipt size={16} className="text-slate-400 flex-shrink-0" />
            <div className="min-w-0"><div className="flex items-center gap-2"><span className="text-xs font-mono text-gray-400">{inv.invoice_number||inv.id}</span><Badge className={`text-[10px] ${statusColor[inv.status]||'bg-gray-100'}`}>{inv.status}</Badge><Badge className="text-[10px] bg-slate-100 text-slate-600">{inv.billing_type}</Badge>{inv.is_retention_invoice && <Badge className="text-[10px] bg-purple-100 text-purple-700">Retention</Badge>}</div><div className="flex gap-3 text-xs text-gray-500 mt-0.5">{inv.invoice_date && <span>{inv.invoice_date}</span>}{inv.due_date && <span>Due: {inv.due_date}</span>}</div></div>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0 ml-3"><div className="text-right"><p className="text-sm font-bold text-slate-900">{fmt(inv.amount)}</p>{inv.retainage_amount > 0 && <p className="text-[10px] text-gray-400">Ret: {fmt(inv.retainage_amount)}</p>}</div><div className="flex gap-1"><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-500" onClick={() => setViewInvoice(inv)} data-testid={`inv-view-${inv.id}`}><Eye size={13} /></Button><Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => startEdit(inv)}><Edit size={13} /></Button><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => handleDelete(inv.id)}><Trash2 size={13} /></Button></div></div>
        </div></CardContent></Card>
      ))}</div>}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// SUBCONTRACTORS TAB
// ═══════════════════════════════════════════════════
const SubcontractorsTab = ({ projectId }) => {
  const [assignments, setAssignments] = useState([]);
  const [allSubs, setAllSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const emptyForm = { subcontractor_id:'', bcs_po_number:'', vendor_quote_number:'', scope_description:'', contract_amount:'', change_orders:'', committed_cost:'', paid_to_date:'', performance_notes:'', quality_issues:'', schedule_issues:'' };
  const [form, setForm] = useState({ ...emptyForm });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, sRes] = await Promise.all([fetch(`${API}/api/crm/projects/${projectId}/subcontractors`, { credentials: 'include' }), fetch(`${API}/api/crm/subcontractors?limit=500`, { credentials: 'include' })]);
      setAssignments((await aRes.json()).assignments || []); setAllSubs((await sRes.json()).subcontractors || []);
    } catch { toast.error('Failed'); } finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    const payload = { ...form, contract_amount:parseFloat(form.contract_amount)||0, change_orders:parseFloat(form.change_orders)||0, committed_cost:parseFloat(form.committed_cost)||parseFloat(form.contract_amount)||0, paid_to_date:parseFloat(form.paid_to_date)||0 };
    try {
      const url = editId ? `${API}/api/crm/projects/${projectId}/subcontractors/${editId}` : `${API}/api/crm/projects/${projectId}/subcontractors`;
      await fetch(url, { method:editId?'PUT':'POST', credentials: 'include', headers:{'Content-Type':'application/json'}, body:JSON.stringify(payload) });
      toast.success(editId?'Updated':'Assigned'); setShowForm(false); setEditId(null); setForm({...emptyForm}); load();
    } catch { toast.error('Failed'); }
  };
  const startEdit = (a) => { setForm({ subcontractor_id:a.subcontractor_id||'', bcs_po_number:a.bcs_po_number||'', vendor_quote_number:a.vendor_quote_number||'', scope_description:a.scope_description||'', contract_amount:a.contract_amount||'', change_orders:a.change_orders||'', committed_cost:a.committed_cost||'', paid_to_date:a.paid_to_date||'', performance_notes:a.performance_notes||'', quality_issues:a.quality_issues||'', schedule_issues:a.schedule_issues||'' }); setEditId(a.id); setShowForm(true); };
  const handleDelete = async (id) => { if (!window.confirm('Remove?')) return; await fetch(`${API}/api/crm/projects/${projectId}/subcontractors/${id}`, { credentials: 'include', method:'DELETE' }); toast.success('Removed'); load(); };
  const totalCommitted = assignments.reduce((s,a)=>s+(a.committed_cost||0),0);

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4 text-sm text-gray-500"><span>{assignments.length} subs</span><span>Committed: <strong className="text-slate-700">{fmt(totalCommitted)}</strong></span></div>
        <Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={() => { setForm({...emptyForm}); setEditId(null); setShowForm(true); }} data-testid="add-sub-assignment-btn"><Plus size={14} className="mr-1" /> Assign Vendor / Sub</Button>
      </div>
      {showForm && (
        <Card className="border-blue-200"><CardContent className="pt-5 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="col-span-2"><Label className="text-xs text-slate-500">Vendor / Subcontractor</Label><select value={form.subcontractor_id} onChange={e => setForm(f => ({...f, subcontractor_id:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="sub-select"><option value="">Select...</option>{allSubs.map(s => <option key={s.id} value={s.id}>{s.company_name} — {s.trade}</option>)}</select></div>
            <CurrencyInput label="Contract Amount" value={form.contract_amount} onChange={v => setForm(f => ({...f, contract_amount:v}))} testId="sub-contract-amt" />
            <CurrencyInput label="Paid to Date" value={form.paid_to_date} onChange={v => setForm(f => ({...f, paid_to_date:v}))} testId="sub-paid" />
            <div><Label className="text-xs text-slate-500">BCS PO #</Label><input value={form.bcs_po_number} onChange={e => setForm(f => ({...f, bcs_po_number:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Auto: MMDDYY-N" data-testid="sub-bcs-po" /></div>
            <div><Label className="text-xs text-slate-500">Vendor Quote #</Label><input value={form.vendor_quote_number} onChange={e => setForm(f => ({...f, vendor_quote_number:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="e.g. Q-2026-1142" data-testid="sub-vendor-quote" /></div>
            <div className="col-span-2"><Label className="text-xs text-slate-500">Scope</Label><input value={form.scope_description} onChange={e => setForm(f => ({...f, scope_description:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
          </div>
          <div className="flex gap-2"><Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={handleSave} data-testid="save-sub-btn"><Save size={14} className="mr-1" /> {editId?'Update':'Assign'}</Button><Button size="sm" variant="outline" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Button></div>
        </CardContent></Card>
      )}
      {loading ? <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto" /></div>
      : assignments.length === 0 ? <Card><CardContent className="py-8 text-center text-gray-400">No vendors / subcontractors assigned</CardContent></Card>
      : <div className="space-y-2">{assignments.map(a => (
        <Card key={a.id}><CardContent className="py-3 px-4"><div className="flex items-center justify-between">
          <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-900">{a.sub_info?.company_name||'Unknown'}</p><div className="flex gap-3 text-xs text-gray-500"><span>{a.sub_info?.trade}</span>{a.bcs_po_number && <span className="font-mono">BCS PO: {a.bcs_po_number}</span>}<span>{a.scope_description}</span></div></div>
          <div className="flex items-center gap-4 flex-shrink-0 ml-3"><div className="text-right"><p className="text-xs text-gray-500">Contract</p><p className="text-sm font-semibold">{fmt(a.contract_amount)}</p></div><div className="text-right"><p className="text-xs text-gray-500">Committed</p><p className="text-sm font-semibold">{fmt(a.committed_cost)}</p></div><div className="flex gap-1">{a.bcs_po_number && <Button variant="outline" size="sm" className="h-7" onClick={() => window.open(`${API}/api/crm/projects/${projectId}/subcontractors/${a.id}/print-po`, '_blank')} data-testid={`print-po-${a.id}`}><Printer size={13} className="mr-1" /> Print PO</Button>}<Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => startEdit(a)}><Edit size={13} /></Button><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => handleDelete(a.id)}><Trash2 size={13} /></Button></div></div>
        </div></CardContent></Card>
      ))}</div>}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// LOGS TAB (Daily / QC / Photos)
// ═══════════════════════════════════════════════════
const LogsTab = ({ projectId }) => {
  const [logType, setLogType] = useState('daily');
  return (
    <div className="space-y-3">
      <div className="flex gap-1 border-b">
        {[['daily','Daily Logs'],['qc','QC Logs'],['photos','Photos']].map(([id,label]) => (
          <button key={id} onClick={() => setLogType(id)} className={`px-4 py-2 text-xs font-medium border-b-2 ${logType===id?'border-blue-900 text-blue-900':'border-transparent text-gray-500'}`}>{label}</button>
        ))}
      </div>
      {logType === 'daily' && <DailyLogsSection projectId={projectId} />}
      {logType === 'qc' && <QCLogsSection projectId={projectId} />}
      {logType === 'photos' && <PhotosSection projectId={projectId} />}
    </div>
  );
};

const DailyLogsSection = ({ projectId }) => {
  const [logs, setLogs] = useState([]); const [loading, setLoading] = useState(true); const [showForm, setShowForm] = useState(false); const [editId, setEditId] = useState(null);
  const emptyForm = { date: new Date().toISOString().slice(0,10), crew:'', work_performed:'', issues:'', notes:'' };
  const [form, setForm] = useState({...emptyForm});
  const load = useCallback(async () => { setLoading(true); try { setLogs((await (await fetch(`${API}/api/crm/projects/${projectId}/daily-logs`, { credentials: 'include' })).json()).logs||[]); } catch {} finally { setLoading(false); } }, [projectId]);
  useEffect(() => { load(); }, [load]);
  const handleSave = async () => { try { const url = editId ? `${API}/api/crm/projects/${projectId}/daily-logs/${editId}` : `${API}/api/crm/projects/${projectId}/daily-logs`; await fetch(url, { method:editId?'PUT':'POST', credentials: 'include', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) }); toast.success('Saved'); setShowForm(false); setEditId(null); setForm({...emptyForm}); load(); } catch { toast.error('Failed'); } };
  const startEdit = l => { setForm({ date:l.date||'', crew:l.crew||'', work_performed:l.work_performed||'', issues:l.issues||'', notes:l.notes||'' }); setEditId(l.id); setShowForm(true); };
  const handleDelete = async id => { if (!window.confirm('Delete?')) return; await fetch(`${API}/api/crm/projects/${projectId}/daily-logs/${id}`, { credentials: 'include', method:'DELETE' }); toast.success('Deleted'); load(); };
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={() => { setForm({...emptyForm}); setEditId(null); setShowForm(true); }} data-testid="add-daily-log-btn"><Plus size={14} className="mr-1" /> Add Log</Button></div>
      {showForm && <Card className="border-blue-200"><CardContent className="pt-5 space-y-3"><div className="grid grid-cols-2 md:grid-cols-4 gap-3"><div><Label className="text-xs text-slate-500">Date</Label><input type="date" value={form.date} onChange={e => setForm(f => ({...f, date:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="dlog-date" /></div><div><Label className="text-xs text-slate-500">Crew</Label><input value={form.crew} onChange={e => setForm(f => ({...f, crew:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div><div className="col-span-2"><Label className="text-xs text-slate-500">Work Performed</Label><input value={form.work_performed} onChange={e => setForm(f => ({...f, work_performed:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="dlog-work" /></div></div><div className="grid grid-cols-2 gap-3"><div><Label className="text-xs text-slate-500">Issues</Label><input value={form.issues} onChange={e => setForm(f => ({...f, issues:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div><div><Label className="text-xs text-slate-500">Notes</Label><input value={form.notes} onChange={e => setForm(f => ({...f, notes:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div></div><div className="flex gap-2"><Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={handleSave} data-testid="save-dlog-btn"><Save size={14} className="mr-1" /> {editId?'Update':'Create'}</Button><Button size="sm" variant="outline" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Button></div></CardContent></Card>}
      {loading ? <div className="text-center py-6"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto" /></div>
      : logs.length === 0 ? <Card><CardContent className="py-6 text-center text-gray-400">No daily logs</CardContent></Card>
      : <div className="space-y-2">{logs.map(l => <Card key={l.id}><CardContent className="py-3 px-4"><div className="flex items-center justify-between"><div className="flex-1"><div className="flex items-center gap-3"><span className="text-xs font-mono text-gray-400">{l.date}</span><span className="text-xs text-gray-500">{l.crew}</span></div><p className="text-sm text-slate-900 mt-0.5">{l.work_performed}</p>{l.issues && <p className="text-xs text-red-500 mt-0.5">Issues: {l.issues}</p>}</div><div className="flex gap-1"><Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => startEdit(l)}><Edit size={13} /></Button><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => handleDelete(l.id)}><Trash2 size={13} /></Button></div></div></CardContent></Card>)}</div>}
    </div>
  );
};

const QCLogsSection = ({ projectId }) => {
  const [logs, setLogs] = useState([]); const [loading, setLoading] = useState(true); const [showForm, setShowForm] = useState(false); const [editId, setEditId] = useState(null);
  const emptyForm = { timestamp: new Date().toISOString().slice(0,16), temperature:'', humidity:'', dew_point:'', surface_profile:'', dft:'', holiday_test:'', inspector:'', pass_fail:'', notes:'' };
  const [form, setForm] = useState({...emptyForm});
  const load = useCallback(async () => { setLoading(true); try { setLogs((await (await fetch(`${API}/api/crm/projects/${projectId}/qc-logs`, { credentials: 'include' })).json()).logs||[]); } catch {} finally { setLoading(false); } }, [projectId]);
  useEffect(() => { load(); }, [load]);
  const handleSave = async () => { try { const url = editId ? `${API}/api/crm/projects/${projectId}/qc-logs/${editId}` : `${API}/api/crm/projects/${projectId}/qc-logs`; await fetch(url, { method:editId?'PUT':'POST', credentials: 'include', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) }); toast.success('Saved'); setShowForm(false); setEditId(null); setForm({...emptyForm}); load(); } catch { toast.error('Failed'); } };
  const startEdit = l => { setForm({ timestamp:l.timestamp||'', temperature:l.temperature||'', humidity:l.humidity||'', dew_point:l.dew_point||'', surface_profile:l.surface_profile||'', dft:l.dft||'', holiday_test:l.holiday_test||'', inspector:l.inspector||'', pass_fail:l.pass_fail||'', notes:l.notes||'' }); setEditId(l.id); setShowForm(true); };
  const handleDelete = async id => { if (!window.confirm('Delete?')) return; await fetch(`${API}/api/crm/projects/${projectId}/qc-logs/${id}`, { credentials: 'include', method:'DELETE' }); toast.success('Deleted'); load(); };
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={() => { setForm({...emptyForm}); setEditId(null); setShowForm(true); }} data-testid="add-qc-log-btn"><Plus size={14} className="mr-1" /> Add QC Log</Button></div>
      {showForm && <Card className="border-blue-200"><CardContent className="pt-5 space-y-3"><div className="grid grid-cols-2 md:grid-cols-4 gap-3"><div><Label className="text-xs text-slate-500">Timestamp</Label><input type="datetime-local" value={form.timestamp} onChange={e => setForm(f => ({...f, timestamp:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="qc-timestamp" /></div><div><Label className="text-xs text-slate-500">Temp (°F)</Label><input value={form.temperature} onChange={e => setForm(f => ({...f, temperature:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div><div><Label className="text-xs text-slate-500">Humidity (%)</Label><input value={form.humidity} onChange={e => setForm(f => ({...f, humidity:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div><div><Label className="text-xs text-slate-500">Dew Point (°F)</Label><input value={form.dew_point} onChange={e => setForm(f => ({...f, dew_point:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div><div><Label className="text-xs text-slate-500">Surface Profile</Label><input value={form.surface_profile} onChange={e => setForm(f => ({...f, surface_profile:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div><div><Label className="text-xs text-slate-500">DFT (mils)</Label><input value={form.dft} onChange={e => setForm(f => ({...f, dft:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div><div><Label className="text-xs text-slate-500">Holiday Test</Label><input value={form.holiday_test} onChange={e => setForm(f => ({...f, holiday_test:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div><div><Label className="text-xs text-slate-500">Inspector</Label><input value={form.inspector} onChange={e => setForm(f => ({...f, inspector:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div><div><Label className="text-xs text-slate-500">Pass/Fail</Label><select value={form.pass_fail} onChange={e => setForm(f => ({...f, pass_fail:e.target.value}))} className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="qc-pass-fail"><option value="">--</option><option>Pass</option><option>Fail</option></select></div></div><div><Label className="text-xs text-slate-500">Notes</Label><textarea value={form.notes} onChange={e => setForm(f => ({...f, notes:e.target.value}))} rows={2} className="w-full px-3 py-2 border rounded-lg text-sm" /></div><div className="flex gap-2"><Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={handleSave} data-testid="save-qc-btn"><Save size={14} className="mr-1" /> {editId?'Update':'Create'}</Button><Button size="sm" variant="outline" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Button></div></CardContent></Card>}
      {loading ? <div className="text-center py-6"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto" /></div>
      : logs.length === 0 ? <Card><CardContent className="py-6 text-center text-gray-400">No QC logs</CardContent></Card>
      : <div className="space-y-2">{logs.map(l => <Card key={l.id}><CardContent className="py-3 px-4"><div className="flex items-center justify-between"><div className="flex-1"><div className="flex items-center gap-2"><span className="text-xs font-mono text-gray-400">{(l.timestamp||'').slice(0,16)}</span><span className="text-xs text-gray-500">{l.inspector}</span>{l.pass_fail && <Badge className={`text-[10px] ${l.pass_fail==='Pass'?'bg-emerald-100 text-emerald-700':'bg-red-100 text-red-700'}`}>{l.pass_fail}</Badge>}</div><div className="flex gap-3 text-xs text-gray-600 mt-1">{l.temperature && <span>Temp: {l.temperature}°F</span>}{l.humidity && <span>RH: {l.humidity}%</span>}{l.dft && <span>DFT: {l.dft}</span>}</div></div><div className="flex gap-1"><Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => startEdit(l)}><Edit size={13} /></Button><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => handleDelete(l.id)}><Trash2 size={13} /></Button></div></div></CardContent></Card>)}</div>}
    </div>
  );
};

const PhotosSection = ({ projectId }) => {
  const [photos, setPhotos] = useState([]); const [loading, setLoading] = useState(true);
  const load = useCallback(async () => { setLoading(true); try { setPhotos((await (await fetch(`${API}/api/crm/projects/${projectId}/photos`, { credentials: 'include' })).json()).photos||[]); } catch {} finally { setLoading(false); } }, [projectId]);
  useEffect(() => { load(); }, [load]);
  const handleUpload = async (e) => { const file = e.target.files[0]; if (!file) return; const fd = new FormData(); fd.append('file', file); try { await fetch(`${API}/api/crm/projects/${projectId}/photos?description=&date=${new Date().toISOString().slice(0,10)}`, { credentials: 'include', method:'POST', body:fd }); toast.success('Uploaded'); load(); } catch { toast.error('Failed'); } e.target.value = ''; };
  const handleDelete = async id => { if (!window.confirm('Delete?')) return; await fetch(`${API}/api/crm/projects/${projectId}/photos/${id}`, { credentials: 'include', method:'DELETE' }); toast.success('Deleted'); load(); };
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-900 text-white rounded-lg text-sm font-medium hover:bg-blue-800" data-testid="upload-photo-btn"><input type="file" className="hidden" accept="image/*" onChange={handleUpload} /><Camera size={14} /> Upload Photo</label></div>
      {loading ? <div className="text-center py-6"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto" /></div>
      : photos.length === 0 ? <Card><CardContent className="py-6 text-center text-gray-400">No photos</CardContent></Card>
      : <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{photos.map(p => <Card key={p.id} className="overflow-hidden"><img src={`${API}/api${encodeURI(p.serve_path)}`} alt={p.filename} className="w-full h-32 object-cover" /><CardContent className="py-2 px-3"><p className="text-xs text-gray-500 truncate">{p.filename}</p><div className="flex justify-between items-center mt-1"><span className="text-[10px] text-gray-400">{p.date}</span><Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => handleDelete(p.id)}><Trash2 size={12} /></Button></div></CardContent></Card>)}</div>}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// DOCUMENTS TAB (BCS Quote, Vendor Quotes with PO #s, Customer PO)
// ═══════════════════════════════════════════════════
const DocumentsTab = ({ project, projectId, onReload }) => {
  const handleFileUpload = async (e, category) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const res = await fetch(`${API}/api/crm/projects/${projectId}/files?category=${category}`, { credentials: 'include', method:'POST', body:fd }); if (!res.ok) throw new Error(); toast.success('Uploaded'); onReload(); }
    catch { toast.error('Upload failed'); } e.target.value = '';
  };
  const handleDeleteFile = async (fileId) => { if (!window.confirm('Delete?')) return; await fetch(`${API}/api/crm/projects/${projectId}/files/${fileId}`, { credentials: 'include', method:'DELETE' }); toast.success('Deleted'); onReload(); };

  const renderFileList = (files, label) => (
    files.length === 0 ? <p className="text-sm text-gray-400">No {label} uploaded yet</p>
    : <div className="space-y-2">{files.map(f => (
      <div key={f.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <FileText size={18} className="text-red-500 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <button onClick={() => window.open(fileUrl(f.serve_path), '_blank')} className="text-sm font-medium text-blue-600 hover:underline truncate block text-left" title={f.filename}>{f.filename}</button>
            <span className="text-xs text-gray-400">{(f.size/1024).toFixed(0)} KB · {(f.uploaded_at||'').slice(0,10)}</span>
          </div>
          {f.po_number && <Badge className="bg-slate-800 text-white text-xs font-mono flex-shrink-0">{f.po_number}</Badge>}
        </div>
        <div className="flex gap-1 flex-shrink-0 ml-2">
          <Button variant="outline" size="sm" onClick={() => window.open(fileUrl(f.serve_path), '_blank')}><ExternalLink size={14} className="mr-1" /> View</Button>
          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteFile(f.id)}><Trash2 size={14} /></Button>
        </div>
      </div>
    ))}</div>
  );

  // Resolve arrays. Back-compat: if the array is empty but a singular field exists, use that.
  const bcsQuotes = (project.bcs_quotes && project.bcs_quotes.length > 0) ? project.bcs_quotes
                  : (project.bcs_quote ? [project.bcs_quote] : []);
  const customerPOs = (project.customer_pos && project.customer_pos.length > 0) ? project.customer_pos
                    : (project.customer_po ? [project.customer_po] : []);

  return (
    <div className="space-y-4">
      <Card><CardContent className="pt-5">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold text-slate-900">BCS Quotes</h3>
          <UploadBtn category="bcs_quote" label="Add BCS Quote" onChange={handleFileUpload} testId="upload-bcs-quote" small />
        </div>
        {renderFileList(bcsQuotes, 'BCS quotes')}
      </CardContent></Card>

      <Card><CardContent className="pt-5">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold text-slate-900">Vendor / Subcontractor Quotes</h3>
          <UploadBtn category="sub_quote" label="Add Quote" onChange={handleFileUpload} testId="upload-sub-quote" small />
        </div>
        {renderFileList(project.sub_quotes || [], 'vendor / subcontractor quotes')}
      </CardContent></Card>

      <Card><CardContent className="pt-5">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold text-slate-900">Customer Purchase Orders</h3>
          <UploadBtn category="customer_po" label="Add Customer PO" onChange={handleFileUpload} testId="upload-customer-po" small />
        </div>
        {renderFileList(customerPOs, 'customer POs')}
      </CardContent></Card>

      <Card><CardContent className="pt-5">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold text-slate-900">Project Invoices</h3>
          <UploadBtn category="vendor_invoice" label="Add Invoice" onChange={handleFileUpload} testId="upload-project-invoice" small />
        </div>
        {renderFileList(project.vendor_invoices || [], 'invoices')}
      </CardContent></Card>

      <Card><CardContent className="pt-5">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold text-slate-900">Paid Invoices / Receipts</h3>
          <UploadBtn category="paid_invoice" label="Add Paid Receipt" onChange={handleFileUpload} testId="upload-paid-invoice" small />
        </div>
        {renderFileList(project.paid_invoices || [], 'paid receipts')}
      </CardContent></Card>

      <Card className="border-emerald-200 bg-emerald-50/30"><CardContent className="pt-5">
        <div className="flex justify-between items-center mb-3">
          <div>
            <h3 className="font-semibold text-slate-900">Project Calculation Spreadsheet</h3>
            <p className="text-xs text-gray-500 mt-0.5">Upload your Excel pricing/calculation file (.xlsx, .xls) — one per project, replaces previous on re-upload.</p>
          </div>
          <UploadBtn category="calc_spreadsheet" label={project.calc_spreadsheet ? 'Replace' : 'Upload Spreadsheet'}
            onChange={handleFileUpload} testId="upload-calc-spreadsheet" accept=".xlsx,.xls,.xlsm,.csv" small />
        </div>
        {project.calc_spreadsheet
          ? renderFileList([project.calc_spreadsheet], 'calculation spreadsheet')
          : <p className="text-sm text-gray-400">No calculation spreadsheet uploaded yet.</p>}
      </CardContent></Card>
    </div>
  );
};

// ═══════════════════════════════════════════════════
// SCHEDULE TAB — Construction Gantt Chart Scheduler
// ═══════════════════════════════════════════════════
const TASK_CATEGORIES = [
  { key: 'mobilization', label: 'Mobilization', color: '#6366f1' },
  { key: 'demolition', label: 'Demolition', color: '#ef4444' },
  { key: 'site_prep', label: 'Site Prep', color: '#f59e0b' },
  { key: 'structural', label: 'Structural', color: '#0ea5e9' },
  { key: 'mechanical', label: 'Mechanical / Piping', color: '#8b5cf6' },
  { key: 'electrical', label: 'Electrical', color: '#eab308' },
  { key: 'coatings', label: 'Coatings / Linings', color: '#10b981' },
  { key: 'concrete', label: 'Concrete / Masonry', color: '#78716c' },
  { key: 'inspection', label: 'Inspection / QC', color: '#f97316' },
  { key: 'punchlist', label: 'Punch List', color: '#ec4899' },
  { key: 'closeout', label: 'Close-Out', color: '#64748b' },
  { key: 'general', label: 'General', color: '#1e3a5f' },
];

const getCatColor = (key) => TASK_CATEGORIES.find(c => c.key === key)?.color || '#1e3a5f';

const ScheduleTab = ({ schedule, projectId, onReload, projectName, customerCompany }) => {
  const emptyTask = { date:'', end_date:'', title:'', details:'', crew:'', duration:'1', category:'general', percent_complete:0, predecessor:'' };
  const [tasks, setTasks] = useState(() => {
    if (schedule.length === 0) return [{ ...emptyTask }];
    return schedule.map(t => ({
      ...emptyTask,
      ...t,
      duration: t.duration || '1',
      category: t.category || 'general',
      percent_complete: t.percent_complete || 0,
      end_date: t.end_date || '',
    }));
  });
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('gantt'); // 'gantt' or 'list'

  const addTask = () => setTasks(t => [...t, { ...emptyTask }]);
  const removeTask = (i) => setTasks(t => t.filter((_,idx) => idx !== i));
  const updateTask = (i, field, val) => setTasks(t => t.map((task,idx) => {
    if (idx !== i) return task;
    const updated = { ...task, [field]: val };
    // Auto-calc end_date from date + duration
    if ((field === 'date' || field === 'duration') && updated.date) {
      const dur = parseInt(updated.duration) || 1;
      const start = new Date(updated.date + 'T00:00:00');
      const end = new Date(start);
      end.setDate(end.getDate() + dur - 1);
      updated.end_date = end.toISOString().split('T')[0];
    }
    // Auto-calc duration from end_date
    if (field === 'end_date' && updated.date && updated.end_date) {
      const start = new Date(updated.date + 'T00:00:00');
      const end = new Date(updated.end_date + 'T00:00:00');
      const diff = Math.round((end - start) / 86400000) + 1;
      if (diff > 0) updated.duration = String(diff);
    }
    return updated;
  }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/crm/projects/${projectId}`, { credentials: 'include',
        method: 'PUT', headers: { 'Content-Type':'application/json' }, body: JSON.stringify({ schedule: tasks }),
      });
      toast.success('Schedule saved'); onReload();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  // ── Gantt chart calculations ──
  const validTasks = tasks.filter(t => t.date);
  const allDates = validTasks.flatMap(t => {
    const s = new Date(t.date + 'T00:00:00');
    const dur = parseInt(t.duration) || 1;
    const e = new Date(s); e.setDate(e.getDate() + dur - 1);
    return [s, e];
  });
  const minDate = allDates.length > 0 ? new Date(Math.min(...allDates)) : new Date();
  const maxDate = allDates.length > 0 ? new Date(Math.max(...allDates)) : new Date();
  // Add buffer
  const chartStart = new Date(minDate); chartStart.setDate(chartStart.getDate() - 1);
  const chartEnd = new Date(maxDate); chartEnd.setDate(chartEnd.getDate() + 2);
  const totalDays = Math.max(Math.round((chartEnd - chartStart) / 86400000), 7);

  const getBarStyle = (task) => {
    if (!task.date) return null;
    const s = new Date(task.date + 'T00:00:00');
    const dur = parseInt(task.duration) || 1;
    const left = ((s - chartStart) / 86400000) / totalDays * 100;
    const width = (dur / totalDays) * 100;
    return { left: `${Math.max(0, left)}%`, width: `${Math.max(0.5, width)}%` };
  };

  // Generate day markers for header
  const dayMarkers = [];
  const markerDate = new Date(chartStart);
  while (markerDate <= chartEnd) {
    dayMarkers.push(new Date(markerDate));
    markerDate.setDate(markerDate.getDate() + 1);
  }

  // Group by week for header labels
  const weekMarkers = [];
  let currentWeekStart = null;
  dayMarkers.forEach((d, i) => {
    if (d.getDay() === 1 || i === 0) {
      currentWeekStart = new Date(d);
      weekMarkers.push({ date: currentWeekStart, dayIndex: i });
    }
  });

  const today = new Date(); today.setHours(0,0,0,0);
  const todayLeft = ((today - chartStart) / 86400000) / totalDays * 100;

  // Stats
  const totalTaskCount = tasks.length;
  const completedTasks = tasks.filter(t => t.percent_complete >= 100).length;
  const avgProgress = tasks.length > 0 ? Math.round(tasks.reduce((s,t) => s + (t.percent_complete || 0), 0) / tasks.length) : 0;

  const handlePrint = () => {
    const pw = window.open('', '_blank');
    const rows = tasks.filter(d => d.date || d.title).map(d => {
      const dateObj = d.date ? new Date(d.date + 'T00:00:00') : null;
      const dayName = dateObj ? SHORT_DAYS[dateObj.getDay()] : '';
      const dateStr = dateObj ? `${dayName}, ${dateObj.getMonth()+1}/${dateObj.getDate()}/${dateObj.getFullYear()}` : '—';
      const cat = TASK_CATEGORIES.find(c => c.key === d.category);
      return `<tr><td style="padding:10px 12px;border-bottom:1px solid #e2e8f0;font-weight:600;white-space:nowrap;font-size:13px;">${dateStr}</td><td style="padding:10px 12px;border-bottom:1px solid #e2e8f0;text-align:center;"><span style="background:${cat?.color || '#1e3a5f'};color:white;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600;">${d.duration||'1'}d</span></td><td style="padding:10px 12px;border-bottom:1px solid #e2e8f0;"><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${cat?.color||'#1e3a5f'};margin-right:6px;"></span><strong>${d.title||'—'}</strong><div style="font-size:11px;color:#64748b;margin-top:1px;">${d.details||''}</div></td><td style="padding:10px 12px;border-bottom:1px solid #e2e8f0;font-size:12px;">${d.crew||'—'}</td><td style="padding:10px 12px;border-bottom:1px solid #e2e8f0;text-align:center;font-size:12px;">${d.percent_complete||0}%</td></tr>`;
    }).join('');

    // Build Gantt chart HTML
    let ganttHtml = '';
    if (validTasks.length > 0) {
      const rowH = 32;
      const labelW = 160;
      const chartW = 700;
      const totalH = tasks.length * rowH + 40;
      const td = today;

      // Date headers
      let dateHeaders = '';
      dayMarkers.forEach((d, i) => {
        const x = labelW + (i / totalDays) * chartW;
        const isWknd = d.getDay() === 0 || d.getDay() === 6;
        const isMon = d.getDay() === 1;
        // Weekend shading
        if (isWknd) {
          dateHeaders += `<rect x="${x}" y="28" width="${chartW/totalDays}" height="${totalH - 28}" fill="#f1f5f9" opacity="0.6"/>`;
        }
        // Vertical grid
        dateHeaders += `<line x1="${x}" y1="28" x2="${x}" y2="${totalH}" stroke="#e2e8f0" stroke-width="0.5"/>`;
        // Date labels (show every Monday or if short timeline)
        if (isMon || totalDays <= 14 || i === 0) {
          dateHeaders += `<text x="${x + 2}" y="22" font-size="9" fill="#94a3b8">${d.getMonth()+1}/${d.getDate()}</text>`;
        }
      });

      // Today line
      const todayX = labelW + ((td - chartStart) / 86400000) / totalDays * chartW;
      let todayLine = '';
      if (todayX >= labelW && todayX <= labelW + chartW) {
        todayLine = `<line x1="${todayX}" y1="28" x2="${todayX}" y2="${totalH}" stroke="#ef4444" stroke-width="1.5" stroke-dasharray="4,3"/>
          <text x="${todayX + 3}" y="22" font-size="8" fill="#ef4444" font-weight="bold">TODAY</text>`;
      }

      // Task bars
      let taskBars = '';
      tasks.forEach((task, i) => {
        const y = 32 + i * rowH;
        const cat = TASK_CATEGORIES.find(c => c.key === task.category);
        const color = cat?.color || '#1e3a5f';
        // Label
        taskBars += `<text x="4" y="${y + 20}" font-size="10" font-weight="600" fill="#1e293b">${(task.title || 'Task ' + (i+1)).substring(0, 22)}</text>`;
        if (task.crew) {
          taskBars += `<text x="${labelW - 4}" y="${y + 20}" font-size="8" fill="#94a3b8" text-anchor="end">${task.crew}</text>`;
        }
        // Bar
        if (task.date) {
          const s = new Date(task.date + 'T00:00:00');
          const dur = parseInt(task.duration) || 1;
          const barX = labelW + ((s - chartStart) / 86400000) / totalDays * chartW;
          const barW = Math.max(8, (dur / totalDays) * chartW);
          const pct = task.percent_complete || 0;
          // Background bar
          taskBars += `<rect x="${barX}" y="${y + 6}" width="${barW}" height="${rowH - 12}" rx="3" fill="${color}" opacity="0.2"/>`;
          // Progress fill
          if (pct > 0) {
            taskBars += `<rect x="${barX}" y="${y + 6}" width="${barW * pct / 100}" height="${rowH - 12}" rx="3" fill="${color}" opacity="0.7"/>`;
          }
          // Border
          taskBars += `<rect x="${barX}" y="${y + 6}" width="${barW}" height="${rowH - 12}" rx="3" fill="none" stroke="${color}" stroke-width="1" opacity="0.5"/>`;
          // Label on bar
          if (barW > 30) {
            taskBars += `<text x="${barX + 4}" y="${y + 21}" font-size="8" font-weight="700" fill="${pct > 40 ? 'white' : color}">${pct}%</text>`;
          }
        }
        // Row separator
        taskBars += `<line x1="0" y1="${y + rowH}" x2="${labelW + chartW}" y2="${y + rowH}" stroke="#e2e8f0" stroke-width="0.5"/>`;
      });

      // Legend
      const usedCats = TASK_CATEGORIES.filter(c => tasks.some(t => t.category === c.key));
      let legendHtml = usedCats.map((c, i) =>
        `<span style="display:inline-flex;align-items:center;gap:4px;margin-right:12px;"><span style="width:10px;height:10px;border-radius:2px;background:${c.color};display:inline-block;"></span><span style="font-size:10px;color:#64748b;">${c.label}</span></span>`
      ).join('');

      ganttHtml = `
        <div style="margin:20px 0 10px;border:1px solid #e2e8f0;border-radius:8px;padding:12px;background:#fafbfc;">
          <svg width="${labelW + chartW}" height="${totalH}" style="display:block;">
            <!-- Grid + dates -->
            ${dateHeaders}
            <!-- Separator line -->
            <line x1="${labelW}" y1="28" x2="${labelW}" y2="${totalH}" stroke="#cbd5e1" stroke-width="1"/>
            <line x1="0" y1="28" x2="${labelW + chartW}" y2="28" stroke="#cbd5e1" stroke-width="1"/>
            <text x="4" y="20" font-size="9" font-weight="700" fill="#64748b" text-transform="uppercase">TASK</text>
            <!-- Today -->
            ${todayLine}
            <!-- Bars -->
            ${taskBars}
          </svg>
          <div style="padding:8px 0 0;border-top:1px solid #e2e8f0;margin-top:8px;">${legendHtml}</div>
        </div>`;
    }

    pw.document.write(`<!DOCTYPE html><html><head><title>${projectName} - Schedule</title><style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#1e293b;}.page{max-width:1000px;margin:0 auto;padding:40px;}.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;padding-bottom:16px;border-bottom:3px solid #1e3a5f;}.title h1{font-size:22px;color:#0f172a;margin-bottom:2px;}.title h2{font-size:15px;color:#1e3a5f;font-weight:400;}.title p{font-size:12px;color:#94a3b8;margin-top:3px;}table{width:100%;border-collapse:collapse;margin-top:10px;}th{background:#1e3a5f;color:white;padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.8px;}tr:nth-child(even) td{background:#f8fafc;}.footer{margin-top:30px;padding-top:12px;border-top:2px solid #1e3a5f;font-size:12px;color:#475569;line-height:1.8;}@media print{.page{padding:20px;}}</style></head><body><div class="page"><div class="header"><div class="title"><h1>${projectName}</h1><h2>${customerCompany||''}</h2><p>Construction Schedule</p></div><div class="logo"><img src="${BCS_LOGO_B64}" alt="BCS" style="width:80px;height:80px;border-radius:8px;" /></div></div>${ganttHtml}<table><thead><tr><th>Start</th><th>Dur.</th><th>Activity / Details</th><th>Crew</th><th>%</th></tr></thead><tbody>${rows}</tbody></table><div class="footer"><strong>BCS, a division of Corrosion Analytics™</strong><br/>(949) 371-9948 | info@bcs-us.com | www.bcs-us.com<br/><br/>${CREDENTIALS}</div></div></body></html>`);
    pw.document.close();
    setTimeout(() => pw.print(), 300);
  };

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Calendar size={18} /> Construction Schedule</h3>
        <div className="flex gap-2">
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            <button onClick={() => setView('gantt')} className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${view === 'gantt' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`} data-testid="view-gantt">Gantt</button>
            <button onClick={() => setView('list')} className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${view === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`} data-testid="view-list">List</button>
          </div>
          <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-schedule"><Printer size={14} className="mr-1" /> Print</Button>
          <Button variant="outline" size="sm" onClick={addTask} data-testid="add-schedule-day"><Plus size={14} className="mr-1" /> Add Task</Button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-slate-50 rounded-lg border p-3 text-center">
          <p className="text-lg font-bold text-slate-900">{totalTaskCount}</p>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Tasks</p>
        </div>
        <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-3 text-center">
          <p className="text-lg font-bold text-emerald-700">{completedTasks}</p>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Complete</p>
        </div>
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-3 text-center">
          <p className="text-lg font-bold text-blue-700">{avgProgress}%</p>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Avg Progress</p>
        </div>
        <div className="bg-slate-50 rounded-lg border p-3 text-center">
          <p className="text-lg font-bold text-slate-700">{validTasks.length > 0 ? `${totalDays - 1}` : '0'}</p>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Duration (Days)</p>
        </div>
      </div>

      {/* ── GANTT CHART VIEW ── */}
      {view === 'gantt' && validTasks.length > 0 && (
        <Card><CardContent className="pt-4 pb-2 overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Timeline header */}
            <div className="flex border-b border-slate-200 mb-1">
              <div className="w-56 flex-shrink-0 px-2 py-1 text-[10px] font-semibold text-slate-500 uppercase">Task</div>
              <div className="flex-1 relative" style={{ height: 32 }}>
                {dayMarkers.map((d, i) => {
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  const isToday = d.toDateString() === today.toDateString();
                  const isMonday = d.getDay() === 1;
                  return (
                    <div key={i} className="absolute top-0 bottom-0 border-r border-slate-100" style={{ left: `${(i / totalDays) * 100}%`, width: `${(1 / totalDays) * 100}%` }}>
                      {(isMonday || totalDays <= 14 || i === 0) && (
                        <span className={`text-[9px] px-0.5 whitespace-nowrap ${isToday ? 'font-bold text-blue-700' : isWeekend ? 'text-slate-300' : 'text-slate-400'}`}>
                          {d.getMonth()+1}/{d.getDate()}
                        </span>
                      )}
                      {isWeekend && <div className="absolute inset-0 bg-slate-50/50" />}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Task rows */}
            {tasks.map((task, i) => {
              const bar = getBarStyle(task);
              const cat = TASK_CATEGORIES.find(c => c.key === task.category);
              const pct = task.percent_complete || 0;
              return (
                <div key={i} className="flex items-center border-b border-slate-100 hover:bg-slate-50/50 group" style={{ height: 36 }}>
                  {/* Task label */}
                  <div className="w-56 flex-shrink-0 px-2 flex items-center gap-1.5 overflow-hidden">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: cat?.color || '#1e3a5f' }} />
                    <span className="text-xs font-medium text-slate-800 truncate">{task.title || `Task ${i+1}`}</span>
                    {task.crew && <span className="text-[9px] text-slate-400 flex-shrink-0">({task.crew})</span>}
                  </div>
                  {/* Bar area */}
                  <div className="flex-1 relative h-full">
                    {/* Weekend shading */}
                    {dayMarkers.map((d, di) => (
                      (d.getDay() === 0 || d.getDay() === 6) && <div key={di} className="absolute top-0 bottom-0 bg-slate-50/80" style={{ left: `${(di / totalDays) * 100}%`, width: `${(1 / totalDays) * 100}%` }} />
                    ))}
                    {/* Today line */}
                    {todayLeft >= 0 && todayLeft <= 100 && (
                      <div className="absolute top-0 bottom-0 w-px bg-red-400 z-20" style={{ left: `${todayLeft}%` }} />
                    )}
                    {/* Gantt bar */}
                    {bar && (
                      <div className="absolute top-1.5 rounded-sm overflow-hidden shadow-sm" style={{ left: bar.left, width: bar.width, height: 20 }}>
                        {/* Background */}
                        <div className="absolute inset-0 rounded-sm opacity-25" style={{ background: cat?.color || '#1e3a5f' }} />
                        {/* Progress fill */}
                        <div className="absolute inset-y-0 left-0 rounded-sm" style={{ width: `${pct}%`, background: cat?.color || '#1e3a5f', opacity: 0.8 }} />
                        {/* Label */}
                        <div className="absolute inset-0 flex items-center px-1">
                          <span className="text-[9px] font-bold text-white drop-shadow-sm truncate">{pct > 0 ? `${pct}%` : ''}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Legend */}
            <div className="flex flex-wrap gap-3 mt-3 pt-2 border-t border-slate-100 px-2">
              {TASK_CATEGORIES.filter(c => tasks.some(t => t.category === c.key)).map(c => (
                <div key={c.key} className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: c.color }} />
                  <span className="text-[10px] text-slate-500">{c.label}</span>
                </div>
              ))}
              <div className="flex items-center gap-1 ml-auto">
                <div className="w-px h-3 bg-red-400" />
                <span className="text-[10px] text-red-400">Today</span>
              </div>
            </div>
          </div>
        </CardContent></Card>
      )}

      {/* ── TASK LIST (Edit form) ── */}
      <Card><CardContent className="pt-4">
        <div className="space-y-2">
          {/* Column headers */}
          <div className="grid grid-cols-12 gap-2 px-3 pb-1 border-b border-slate-200">
            <span className="col-span-2 text-[10px] font-semibold text-slate-400 uppercase">Start Date</span>
            <span className="col-span-1 text-[10px] font-semibold text-slate-400 uppercase">Days</span>
            <span className="col-span-2 text-[10px] font-semibold text-slate-400 uppercase">Category</span>
            <span className="col-span-2 text-[10px] font-semibold text-slate-400 uppercase">Activity</span>
            <span className="col-span-2 text-[10px] font-semibold text-slate-400 uppercase">Details</span>
            <span className="col-span-1 text-[10px] font-semibold text-slate-400 uppercase">Crew</span>
            <span className="col-span-1 text-[10px] font-semibold text-slate-400 uppercase">% Done</span>
            <span className="col-span-1"></span>
          </div>

          {tasks.map((task, i) => {
            const dateObj = task.date ? new Date(task.date + 'T00:00:00') : null;
            const dayLabel = dateObj ? SHORT_DAYS[dateObj.getDay()] : '';
            const cat = TASK_CATEGORIES.find(c => c.key === task.category);
            return (
              <div key={i} className="grid grid-cols-12 gap-2 items-center px-3 py-2 rounded-lg border bg-white hover:shadow-sm transition-shadow" data-testid={`schedule-task-${i}`}>
                <div className="col-span-2">
                  <input type="date" value={task.date} onChange={e => updateTask(i,'date',e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" />
                  {dayLabel && <span className="text-[10px] font-medium text-blue-600 mt-0.5 block">{dayLabel}</span>}
                </div>
                <div className="col-span-1">
                  <input type="number" min="1" value={task.duration||'1'} onChange={e => updateTask(i,'duration',e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm text-center" />
                </div>
                <div className="col-span-2">
                  <select value={task.category || 'general'} onChange={e => updateTask(i,'category',e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm bg-white" style={{ borderLeft: `3px solid ${cat?.color || '#1e3a5f'}` }}>
                    {TASK_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <input value={task.title} onChange={e => updateTask(i,'title',e.target.value)} placeholder="Task name" className="w-full px-2 py-1.5 border rounded text-sm font-medium" />
                </div>
                <div className="col-span-2">
                  <input value={task.details} onChange={e => updateTask(i,'details',e.target.value)} placeholder="Notes" className="w-full px-2 py-1.5 border rounded text-sm" />
                </div>
                <div className="col-span-1">
                  <input value={task.crew} onChange={e => updateTask(i,'crew',e.target.value)} placeholder="Crew" className="w-full px-2 py-1.5 border rounded text-sm" />
                </div>
                <div className="col-span-1">
                  <div className="flex items-center gap-1">
                    <input type="range" min="0" max="100" step="5" value={task.percent_complete || 0} onChange={e => updateTask(i,'percent_complete',parseInt(e.target.value))} className="w-full h-1.5 accent-blue-900" />
                    <span className="text-[10px] text-slate-500 w-7 text-right">{task.percent_complete||0}%</span>
                  </div>
                </div>
                <div className="col-span-1 flex justify-end">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => removeTask(i)}><X size={14} /></Button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-3 mt-4">
          <Button onClick={handleSave} disabled={saving} className="bg-blue-900 hover:bg-blue-800" data-testid="save-schedule"><Save size={16} className="mr-1" /> {saving ? 'Saving...' : 'Save Schedule'}</Button>
          <Button variant="outline" size="sm" onClick={addTask}><Plus size={14} className="mr-1" /> Add Task</Button>
        </div>
      </CardContent></Card>
    </div>
  );
};

// ── Helpers ──
function fileUrl(servePath) { return `${API}/api${encodeURI(servePath)}`; }

const FileRow = ({ file, onDelete }) => (
  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
    <div className="flex items-center gap-3 min-w-0 flex-1">
      <FileText size={18} className="text-red-500 flex-shrink-0" />
      <div className="min-w-0">
        <button onClick={() => window.open(fileUrl(file.serve_path), '_blank')} className="text-sm font-medium text-blue-600 hover:underline truncate block text-left" title={file.filename}>{file.filename}</button>
        <span className="text-xs text-gray-400">{(file.size/1024).toFixed(0)} KB</span>
      </div>
    </div>
    <div className="flex gap-1 flex-shrink-0 ml-2">
      <Button variant="outline" size="sm" onClick={() => window.open(fileUrl(file.serve_path), '_blank')}><ExternalLink size={14} className="mr-1" /> View</Button>
      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => onDelete(file.id)}><Trash2 size={14} /></Button>
    </div>
  </div>
);

const UploadBtn = ({ category, label, onChange, testId, small, accept }) => (
  <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-sm font-medium hover:bg-gray-50" data-testid={testId}>
    <input type="file" className="hidden" accept={accept || ".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg"} onChange={e => onChange(e, category)} />
    <Upload size={small ? 14 : 16} /> {label}
  </label>
);

export default CRMProjectDetail;
